import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialLanguageField } from '@app/modules/social-export/components/inputs/SocialLanguageField';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TitleAndContentFieldsMui } from '@app/modules/social-export/components/inputs/TitleAndContentFieldsMui';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { YoutubePlaylistsField } from '@app/modules/social-export/platforms/youtube/playlist-field/YoutubePlaylistsField';
import { YoutubeCategoryField } from '@app/modules/social-export/platforms/youtube/YoutubeCategoryField';
import { YoutubeMadeForKidsField } from '@app/modules/social-export/platforms/youtube/YoutubeMadeForKidsField';
import { YoutubePrivacyStatusField } from '@app/modules/social-export/platforms/youtube/YoutubePrivacyStatusField';
import { FormSectionV2 } from '@app/core/components/FormSectionV2';
import { zodResolver } from '@hookform/resolvers/zod';
import { socialExport } from '@mi-front/api-clients';
import { Grid } from '@mui/material';
import React from 'react';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { FormattedMessage } from 'react-intl';
import { useYoutubePublishSchema, YoutubePublishValues } from './useYoutubePublishSchema';

type YoutubePublishFormProps = BaseExportFormProps<
  socialExport.YoutubePublishDto,
  socialExport.YoutubeSettingsDto
>;

export function YoutubePublishForm({
  source,
  onUpload,
  defaultSettings,
  videoUrl,
  connectionId,
  actions,
}: YoutubePublishFormProps) {
  const schema = useYoutubePublishSchema();
  const initialValues: YoutubePublishValues = {
    title: source.title ?? '',
    content: source.description ?? '',
    tags: source.tags.join(', '),
    thumbnailUrl: undefined,
    thumbType: 'DEFAULT',
    language: defaultSettings?.language ?? null,
    privacyStatus: defaultSettings?.privacyStatus ?? 'private',
    madeForKids: defaultSettings?.madeForKids ?? false,
    publishAt: null,
    categoryId: defaultSettings?.categoryId ?? '',
    playlistsIds: [],
  };

  const beforeSubmit = (data: YoutubePublishValues): socialExport.YoutubePublishDto => {
    return {
      title: data.title,
      description: data.content,
      tags: data.tags
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      thumbnailUrl:
        data.thumbType === 'IMAGE_URL' || data.thumbType === 'VIDEO_FRAME'
          ? data.thumbnailUrl
          : undefined,
      language: data.language,
      privacyStatus: data.privacyStatus as socialExport.YoutubePublishDto['privacyStatus'],
      madeForKids: data.madeForKids,
      publishAt: data.publishAt || undefined,
      categoryId: data.categoryId || undefined,
      playlistsIds: (data.playlistsIds ?? []).map((playlist) => playlist.id),
    };
  };

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      onSuccess={async (formData) => {
        const processedData = beforeSubmit(formData);
        await onUpload(processedData);
      }}
      resolver={zodResolver(schema)}
    >
      <PublishFormUnsavedChangesWarning />
      <FormSectionV2
        title={<FormattedMessage id="socialExport.form.content" defaultMessage="Contenido" />}
      >
        <Grid container>
          <Grid item md={12}>
            <TitleAndContentFieldsMui />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <TextFieldElement
              name="tags"
              label={<FormattedMessage id="socialExport.form.tags" defaultMessage="Tags" />}
              helperText={'Valores separados por coma'}
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={10}>
            <YoutubePlaylistsField connectionId={connectionId} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={10}>
            <ThumbFieldsMui showFrameImageToField={true} videoUrl={videoUrl} />
          </Grid>
        </Grid>
      </FormSectionV2>
      <FormSectionV2
        title={
          <FormattedMessage
            id="socialExport.form.settings"
            defaultMessage="Parámetros de publicación"
          />
        }
      >
        <Grid container>
          <Grid item md={8}>
            <YoutubeCategoryField connectionId={connectionId} />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <SocialLanguageField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={8}>
            <YoutubePrivacyStatusField />
          </Grid>
        </Grid>
        <Grid container>
          <Grid item md={12}>
            <YoutubeMadeForKidsField />
          </Grid>
        </Grid>
        <Grid container mt={-4}>
          <Grid item md={12}>
            <SocialPublishAtField />
          </Grid>
        </Grid>
      </FormSectionV2>
      {actions}
    </FormContainer>
  );
}
